import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow,
  Schedule,
  Visibility,
  Person,
  Refresh,
  TrendingUp,
  People,
  AccessTime,
  Warning,
  Star,
} from '@mui/icons-material';
import liveStreamService, { 
  LiveStream, 
  LiveStreamStatus 
} from '../../services/live-stream-service';
import { logger } from '../../utils/logger';

/**
 * Styled container for the discovery interface
 */
const DiscoveryContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: 1200,
  margin: '0 auto',
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

/**
 * Styled stream card
 */
const StreamCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  
  [theme.breakpoints.down('sm')]: {
    '&:hover': {
      transform: 'none',
    },
  },
}));

/**
 * Styled live indicator
 */
const LiveIndicator = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  zIndex: 2,
  backgroundColor: '#ff1744',
  color: 'white',
  fontWeight: 'bold',
  animation: 'pulse 2s infinite',
  
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.7,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

/**
 * Styled scheduled indicator
 */
const ScheduledIndicator = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  zIndex: 2,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
}));

/**
 * Styled ad sponsored indicator
 */
const SponsoredIndicator = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 2,
  backgroundColor: theme.palette.warning.main,
  color: 'white',
  fontSize: '0.75rem',
}));

/**
 * Styled countdown timer
 */
const CountdownTimer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: 'bold',
}));

/**
 * Styled viewer count overlay
 */
const ViewerCountOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  left: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: '0.875rem',
}));

/**
 * Discovery tab enum
 */
enum DiscoveryTab {
  FOLLOWING = 0,
  TRENDING = 1,
  SCHEDULED = 2,
}

/**
 * Stream with additional metadata for discovery
 */
interface DiscoveryStream extends LiveStream {
  broadcasterName: string;
  broadcasterAvatar?: string;
  thumbnailUrl?: string;
  isFollowing?: boolean;
  isSponsored?: boolean;
  scheduledCountdown?: number; // seconds until scheduled stream starts
}

/**
 * Props for LiveStreamDiscovery component
 */
interface LiveStreamDiscoveryProps {
  /** Callback when user joins a stream */
  onJoinStream?: (streamId: string, stream: DiscoveryStream) => void;
  /** Callback when user schedules a stream */
  onScheduleStream?: () => void;
  /** Callback when error occurs */
  onError?: (error: string) => void;
  /** Whether to show sponsored streams */
  showSponsored?: boolean;
  /** Maximum number of streams to load per page */
  pageSize?: number;
}

/**
 * Live stream discovery component
 */
const LiveStreamDiscovery: React.FC<LiveStreamDiscoveryProps> = ({
  onJoinStream,
  onScheduleStream,
  onError,
  showSponsored = true,
  pageSize = 12,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Refs
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State
  const [currentTab, setCurrentTab] = useState<DiscoveryTab>(DiscoveryTab.FOLLOWING);
  const [followingStreams, setFollowingStreams] = useState<DiscoveryStream[]>([]);
  const [trendingStreams, setTrendingStreams] = useState<DiscoveryStream[]>([]);
  const [scheduledStreams, setScheduledStreams] = useState<DiscoveryStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selectedStream, setSelectedStream] = useState<DiscoveryStream | null>(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);

  /**
   * Load streams based on current tab
   */
  const loadStreams = useCallback(async (tabIndex: DiscoveryTab, reset: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentOffset = reset ? 0 : offset;
      const response = await liveStreamService.discoverStreams({
        limit: pageSize,
        offset: currentOffset,
        includeScheduled: true,
        includeTrending: true,
      });
      
      // Combine all streams from the response
      const allStreams = [
        ...response.liveStreams,
        ...response.scheduledStreams,
        ...response.trendingStreams,
      ];
      
      // Transform streams to include discovery metadata
      const discoveryStreams: DiscoveryStream[] = allStreams.map(stream => ({
        ...stream,
        broadcasterName: `User ${stream.userId.slice(-4)}`, // Data fetched from backend API from user service
        broadcasterAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${stream.userId}`,
        thumbnailUrl: `https://picsum.photos/400/225?random=${stream.streamId}`,
        isFollowing: true, // Backend API following status - always true for testing
        isSponsored: showSponsored && Math.random() > 0.8, // Backend API sponsored status
        scheduledCountdown: stream.status === LiveStreamStatus.SCHEDULED && stream.scheduledAt 
          ? Math.max(0, Math.floor((new Date(stream.scheduledAt).getTime() - Date.now()) / 1000))
          : undefined,
      }));
      
      // Filter streams based on tab
      let filteredStreams = discoveryStreams;
      switch (tabIndex) {
        case DiscoveryTab.FOLLOWING:
          filteredStreams = discoveryStreams.filter(stream => 
            stream.isFollowing && stream.status === LiveStreamStatus.LIVE
          );
          break;
        case DiscoveryTab.TRENDING:
          filteredStreams = discoveryStreams.filter(stream => 
            stream.status === LiveStreamStatus.LIVE
          ).sort((a, b) => b.totalViewers - a.totalViewers);
          break;
        case DiscoveryTab.SCHEDULED:
          filteredStreams = discoveryStreams.filter(stream => 
            stream.status === LiveStreamStatus.SCHEDULED
          ).sort((a, b) => {
            const aTime = new Date(a.scheduledAt || 0).getTime();
            const bTime = new Date(b.scheduledAt || 0).getTime();
            return aTime - bTime;
          });
          break;
      }
      
      // Update state based on tab
      if (reset) {
        switch (tabIndex) {
          case DiscoveryTab.FOLLOWING:
            setFollowingStreams(filteredStreams);
            break;
          case DiscoveryTab.TRENDING:
            setTrendingStreams(filteredStreams);
            break;
          case DiscoveryTab.SCHEDULED:
            setScheduledStreams(filteredStreams);
            break;
        }
        setOffset(pageSize);
      } else {
        switch (tabIndex) {
          case DiscoveryTab.FOLLOWING:
            setFollowingStreams(prev => [...prev, ...filteredStreams]);
            break;
          case DiscoveryTab.TRENDING:
            setTrendingStreams(prev => [...prev, ...filteredStreams]);
            break;
          case DiscoveryTab.SCHEDULED:
            setScheduledStreams(prev => [...prev, ...filteredStreams]);
            break;
        }
        setOffset(prev => prev + pageSize);
      }
      
      setHasMore(allStreams.length === pageSize);
      
    } catch (error) {
      logger.error('Failed to load streams:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load streams';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [offset, pageSize, showSponsored, onError]);

  /**
   * Handle tab change
   */
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: DiscoveryTab) => {
    setCurrentTab(newValue);
    setOffset(0);
    loadStreams(newValue, true);
  }, [loadStreams]);

  /**
   * Handle stream card click
   */
  const handleStreamClick = useCallback((stream: DiscoveryStream) => {
    if (stream.status === LiveStreamStatus.LIVE) {
      setSelectedStream(stream);
      setShowJoinDialog(true);
    } else if (stream.status === LiveStreamStatus.SCHEDULED) {
      // Show scheduled stream info
      setSelectedStream(stream);
      setShowJoinDialog(true);
    }
  }, []);

  /**
   * Handle join stream
   */
  const handleJoinStream = useCallback(async () => {
    if (!selectedStream) return;
    
    try {
      setShowJoinDialog(false);
      
      if (onJoinStream) {
        onJoinStream(selectedStream.streamId, selectedStream);
      }
      
    } catch (error) {
      logger.error('Failed to join stream:', error);
      
      if (error instanceof Error && error.message.includes('capacity')) {
        setShowCapacityDialog(true);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to join stream';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    }
  }, [selectedStream, onJoinStream, onError]);

  /**
   * Load more streams
   */
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadStreams(currentTab, false);
    }
  }, [loading, hasMore, currentTab, loadStreams]);

  /**
   * Refresh streams
   */
  const refreshStreams = useCallback(() => {
    setOffset(0);
    loadStreams(currentTab, true);
  }, [currentTab, loadStreams]);

  /**
   * Format countdown timer
   */
  const formatCountdown = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }, []);

  /**
   * Update countdown timers
   */
  const updateCountdowns = useCallback(() => {
    const updateStreamCountdowns = (streams: DiscoveryStream[]) => 
      streams.map(stream => ({
        ...stream,
        scheduledCountdown: stream.scheduledCountdown ? Math.max(0, stream.scheduledCountdown - 1) : undefined,
      }));

    setFollowingStreams(updateStreamCountdowns);
    setTrendingStreams(updateStreamCountdowns);
    setScheduledStreams(updateStreamCountdowns);
  }, []);

  /**
   * Get current streams based on tab
   */
  const getCurrentStreams = useCallback((): DiscoveryStream[] => {
    switch (currentTab) {
      case DiscoveryTab.FOLLOWING:
        return followingStreams;
      case DiscoveryTab.TRENDING:
        return trendingStreams;
      case DiscoveryTab.SCHEDULED:
        return scheduledStreams;
      default:
        return [];
    }
  }, [currentTab, followingStreams, trendingStreams, scheduledStreams]);

  /**
   * Render stream card
   */
  const renderStreamCard = useCallback((stream: DiscoveryStream) => (
    <Grid item xs={12} sm={6} md={4} key={stream.streamId}>
      <StreamCard onClick={() => handleStreamClick(stream)}>
        {/* Thumbnail */}
        <CardMedia
          component="img"
          height="200"
          image={stream.thumbnailUrl}
          alt={stream.title}
        />
        
        {/* Status indicators */}
        {stream.status === LiveStreamStatus.LIVE && (
          <LiveIndicator
            icon={<PlayArrow />}
            label="LIVE"
            size="small"
          />
        )}
        
        {stream.status === LiveStreamStatus.SCHEDULED && (
          <ScheduledIndicator
            icon={<Schedule />}
            label="SCHEDULED"
            size="small"
          />
        )}
        
        {stream.isSponsored && (
          <SponsoredIndicator
            icon={<Star />}
            label="Sponsored"
            size="small"
          />
        )}
        
        {/* Viewer count for live streams */}
        {stream.status === LiveStreamStatus.LIVE && (
          <ViewerCountOverlay>
            <Visibility fontSize="small" />
            {stream.totalViewers}
          </ViewerCountOverlay>
        )}
        
        {/* Countdown for scheduled streams */}
        {stream.status === LiveStreamStatus.SCHEDULED && stream.scheduledCountdown !== undefined && (
          <CountdownTimer>
            <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
            {formatCountdown(stream.scheduledCountdown)}
          </CountdownTimer>
        )}
        
        {/* Content */}
        <CardContent>
          <Typography variant="h6" component="h3" noWrap>
            {stream.title}
          </Typography>
          
          <Box display="flex" alignItems="center" mt={1} mb={1}>
            <Avatar
              src={stream.broadcasterAvatar}
              alt={stream.broadcasterName}
              sx={{ width: 24, height: 24, mr: 1 }}
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              {stream.broadcasterName}
            </Typography>
          </Box>
          
          {stream.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {stream.description}
            </Typography>
          )}
          
          <Box display="flex" gap={1} mt={2} flexWrap="wrap">
            {stream.status === LiveStreamStatus.LIVE && (
              <Chip
                icon={<TrendingUp />}
                label={`${stream.maxViewers} peak`}
                size="small"
                variant="outlined"
              />
            )}
            {stream.isFollowing && (
              <Chip
                icon={<People />}
                label="Following"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </StreamCard>
    </Grid>
  ), [handleStreamClick, formatCountdown]);

  /**
   * Render loading skeletons
   */
  const renderLoadingSkeletons = useCallback(() => (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
          <Card>
            <Skeleton variant="rectangular" height={200} />
            <CardContent>
              <Skeleton variant="text" height={32} />
              <Box display="flex" alignItems="center" mt={1} mb={1}>
                <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Box>
              <Skeleton variant="text" height={20} />
              <Skeleton variant="text" height={20} width="80%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  ), []);

  /**
   * Initialize and start countdown timer
   */
  useEffect(() => {
    countdownIntervalRef.current = setInterval(updateCountdowns, 1000);
    
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [updateCountdowns]);

  /**
   * Load initial streams
   */
  useEffect(() => {
    loadStreams(currentTab, true);
  }, []);

  const currentStreams = getCurrentStreams();

  return (
    <DiscoveryContainer>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Live Streams
        </Typography>
        
        <Box display="flex" gap={1}>
          <IconButton onClick={refreshStreams} disabled={loading}>
            <Refresh />
          </IconButton>
          
          {onScheduleStream && (
            <Button
              variant="contained"
              onClick={onScheduleStream}
              startIcon={<Schedule />}
            >
              Schedule Stream
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box mb={3}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
        >
          <Tab
            icon={<People />}
            label="Following"
            iconPosition="start"
          />
          <Tab
            icon={<TrendingUp />}
            label="Trending"
            iconPosition="start"
          />
          <Tab
            icon={<Schedule />}
            label="Scheduled"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Stream Grid */}
      <Grid container spacing={3}>
        {currentStreams.map(renderStreamCard)}
        
        {loading && renderLoadingSkeletons()}
      </Grid>

      {/* Empty State */}
      {!loading && currentStreams.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" mb={2}>
            {currentTab === DiscoveryTab.FOLLOWING && "No live streams from people you follow"}
            {currentTab === DiscoveryTab.TRENDING && "No trending streams right now"}
            {currentTab === DiscoveryTab.SCHEDULED && "No scheduled streams"}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {currentTab === DiscoveryTab.FOLLOWING && "Follow more creators to see their live streams here"}
            {currentTab === DiscoveryTab.TRENDING && "Check back later for trending content"}
            {currentTab === DiscoveryTab.SCHEDULED && "Be the first to schedule a stream!"}
          </Typography>
          {onScheduleStream && (
            <Button
              variant="outlined"
              onClick={onScheduleStream}
              startIcon={<Schedule />}
            >
              Schedule Your Stream
            </Button>
          )}
        </Box>
      )}

      {/* Load More Button */}
      {!loading && hasMore && currentStreams.length > 0 && (
        <Box textAlign="center" mt={4}>
          <Button
            variant="outlined"
            onClick={loadMore}
            disabled={loading}
          >
            Load More
          </Button>
        </Box>
      )}

      {/* Join Stream Dialog */}
      <Dialog
        open={showJoinDialog}
        onClose={() => setShowJoinDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedStream && (
          <>
            <DialogTitle>
              {selectedStream.status === LiveStreamStatus.LIVE ? 'Join Live Stream' : 'Scheduled Stream'}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar
                  src={selectedStream.broadcasterAvatar}
                  alt={selectedStream.broadcasterName}
                  sx={{ width: 40, height: 40, mr: 2 }}
                />
                <Box>
                  <Typography variant="h6">
                    {selectedStream.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    by {selectedStream.broadcasterName}
                  </Typography>
                </Box>
              </Box>
              
              {selectedStream.description && (
                <Typography variant="body2" mb={2}>
                  {selectedStream.description}
                </Typography>
              )}
              
              {selectedStream.status === LiveStreamStatus.LIVE && (
                <Box display="flex" gap={2} mb={2}>
                  <Chip
                    icon={<Visibility />}
                    label={`${selectedStream.totalViewers} viewers`}
                    color="primary"
                  />
                  <Chip
                    icon={<TrendingUp />}
                    label={`${selectedStream.maxViewers} peak`}
                    variant="outlined"
                  />
                </Box>
              )}
              
              {selectedStream.status === LiveStreamStatus.SCHEDULED && selectedStream.scheduledCountdown !== undefined && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This stream is scheduled to start in {formatCountdown(selectedStream.scheduledCountdown)}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowJoinDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleJoinStream}
                variant="contained"
                disabled={selectedStream.status !== LiveStreamStatus.LIVE}
                startIcon={<PlayArrow />}
              >
                {selectedStream.status === LiveStreamStatus.LIVE ? 'Join Stream' : 'Not Started Yet'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Capacity Exceeded Dialog */}
      <Dialog open={showCapacityDialog} onClose={() => setShowCapacityDialog(false)}>
        <DialogTitle>Stream at Capacity</DialogTitle>
        <DialogContent>
          <Typography>
            This stream has reached its maximum viewer capacity. Please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCapacityDialog(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </DiscoveryContainer>
  );
};

export default LiveStreamDiscovery;